import React from 'react';
import { CSSObject } from '@emotion/react';
import { injectTestId } from '@cvent/nucleus-test-automation';
import Link from 'next/link';
import { useTranslate } from 'nucleus-text';
import useBreakpoints from '@hooks/useBreakpoints';

interface EventLogoProps {
  logoSrc?: string;
  logoLink?: string;
  logoWidth?: number;
  testID?: string;
}

const useStyles = ({ logoWidth }): Record<string, CSSObject> => {
  const { isMobile } = useBreakpoints();
  const logoStartPadding = 24;
  const logoEndPadding = 8;

  const logoIconWidth = typeof logoWidth !== 'undefined' ? logoWidth - (logoStartPadding + logoEndPadding) : 220;

  return {
    logo: {
      width: logoWidth,
      maxHeight: isMobile ? 74 : 114,
      maxWidth: isMobile ? 140 : logoIconWidth,
      minHeight: isMobile ? 25 : 44,
      minWidth: isMobile ? 50 : 75,
      objectFit: 'contain'
    },
    logoImg: {
      maxHeight: isMobile ? 74 : 114,
      maxWidth: isMobile ? 140 : 220,
      minHeight: isMobile ? 25 : 44,
      minWidth: isMobile ? 50 : 75,
      objectFit: 'contain',
      verticalAlign: 'bottom'
    }
  };
};

function EventLogo({ logoSrc, logoLink, logoWidth, testID = 'logo-image' }: EventLogoProps): JSX.Element {
  const { translate } = useTranslate();
  const styles = useStyles({
    logoWidth
  });

  if (!logoSrc) {
    return null;
  }

  /* The event logo image. If the image is going to be wrapped in a link, 
     the alt text is My Event to indicate to screen reader users that the image 
     link will take them back to the event homepage.
     If the image is not going to be wrapped in a link, the alt text is blank 
     (since the image is just decorative). */
  const eventLogo = (
    <img
      src={logoSrc}
      css={styles.logoImg}
      alt={logoLink ? translate('Navigation-Tab-MyEvent') : ''}
      {...injectTestId(testID)}
    />
  );

  /* If there is a link, wrap the event logo in the link */
  return logoLink ? (
    <Link href={logoLink} passHref {...injectTestId('event-logo-link')} css={styles.logo} tabIndex={0}>
      {eventLogo}
    </Link>
  ) : (
    eventLogo
  );
}

export default EventLogo;
